// src/pages/HistoryPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, History, ChevronRight, Plus } from 'lucide-react';

import { getWatchedHistory } from '../services/profileService';
import VerticalContentCard from '../components/Category/VerticalContentCard'; 
import { useAuthContext } from '../context/AuthContext'; 
import { convertToPersianNumbers } from '../utils/PersianNumberUtils';

const HistoryPage = () => {
    const navigate = useNavigate();
    const { profileUpdateKey } = useAuthContext();
    const [historyItems, setHistoryItems] = useState([]);      // لیست کل تاریخچه
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false); // بارگذاری صفحات بعدی
    const [isError, setIsError] = useState(false);
    const [nextPageUrl, setNextPageUrl] = useState(null);      // لینک صفحه بعدی از بک‌اند
    const [errorStatus, setErrorStatus] = useState(null);

    // تابع اصلی فراخوانی داده‌های تاریخچه (با پشتیبانی از صفحه‌بندی)
    const fetchHistory = useCallback(async (url = null) => {
        try {
            if (!url) setIsLoading(true);
            else setIsFetchingMore(true);

            const responseData = await getWatchedHistory(url);

            // استخراج آیتم‌ها (با فرض اینکه responseData.results آرایه است)
            const rawItems = responseData?.results || (Array.isArray(responseData) ? responseData : []);
            const products = rawItems
                .map(item => item?.product_details || item?.product || item)
                .filter(p => p !== null && typeof p === 'object')
                .map(p => ({ ...p, id: p.id })); // اطمینان از وجود id

            // اضافه کردن به لیست (صفحه اول جایگزین، صفحات بعدی الحاق)
            setHistoryItems(prev => url ? [...prev, ...products] : products);

            // ذخیره لینک صفحه بعدی
            setNextPageUrl(responseData?.next || null);
            setErrorStatus(null);
            setIsError(false);
        } catch (error) {
            console.error("PAGINATION ERROR (History):", error);
            setIsError(true);
            if (error.response) setErrorStatus(error.response.status);
        } finally {
            setIsLoading(false);
            setIsFetchingMore(false);
        }
    }, []);

    // اجرای اولیه با تغییر کلید آپدیت پروفایل
    useEffect(() => {
        fetchHistory();
    }, [fetchHistory, profileUpdateKey]);

    const handleLoadMore = () => {
        if (nextPageUrl && !isFetchingMore) {
            fetchHistory(nextPageUrl);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col justify-center items-center min-h-[400px] rtl">
                <Loader2 className="w-10 h-10 animate-spin text-purple-700 mb-4" />
                <p className="text-gray-500 font-medium">در حال بارگذاری تاریخچه بازدیدها...</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex justify-center items-center min-h-[400px] rtl">
                <div className="bg-red-50 text-red-700 p-4 rounded-lg text-center">
                    {errorStatus === 401
                        ? 'برای مشاهده تاریخچه باید وارد شوید.'
                        : 'خطا در دریافت تاریخچه بازدید.'}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 rtl text-right font-vazir">
            {/* Header Section (مشابه FavoritesFullPage) */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4 border-b pb-6">
                <div>
                    <nav className="flex items-center text-xs text-gray-400 mb-2 font-medium">
                        <Link to="/profile" className="hover:text-purple-600 transition">پروفایل کاربری</Link>
                        <ChevronRight className="w-3 h-3 mx-1 rotate-180" />
                        <span className="text-gray-600">تاریخچه بازدید</span>
                    </nav>
                    <h1 className="text-xs font-black text-gray-800 tracking-tight">تاریخچه مشاهده محتوا</h1>
                </div>

                <div className="bg-purple-50 text-purple-700 border border-purple-100 px-5 py-2 rounded-2xl flex items-center shadow-sm">
                    <span className="text-xl font-bold ml-2">{convertToPersianNumbers(historyItems.length)}</span>
                    <span className="text-[11px] font-medium">مورد را مشاهده کرده‌اید</span>
                </div>
            </div>

            {/* محتوای اصلی */}
            {historyItems.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {historyItems.map((product, index) => (
                            <div key={`${product.id}-${index}`} className="transition-all duration-300">
                                <VerticalContentCard data={product} />
                            </div>
                        ))}
                    </div>

                    {/* دکمه مشاهده بیشتر (در صورت وجود صفحه بعدی) */}
                    {nextPageUrl && (
                        <div className="flex justify-center mt-12 pb-10">
                            <button
                                onClick={handleLoadMore}
                                disabled={isFetchingMore}
                                className="flex items-center space-x-2 space-x-reverse bg-white border-2 border-purple-600 text-purple-600 px-8 py-3 rounded-xl font-bold hover:bg-purple-600 hover:text-white transition-all duration-300 disabled:opacity-50 shadow-lg shadow-purple-50"
                            >
                                {isFetchingMore ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>در حال بارگذاری...</span>
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-5 h-5" />
                                        <span>مشاهده موارد بیشتر</span>
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                /* حالت خالی */
                <div className="flex flex-col items-center justify-center py-24 bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-100">
                    <History className="w-16 h-16 text-gray-300 mb-6" />
                    <h2 className="text-xl font-bold text-gray-800 mb-2">هنوز محتوایی مشاهده نکرده‌اید</h2>
                    <Link to="/" className="mt-6 text-purple-700 font-bold underline">
                        بازگشت به صفحه اصلی و کشف محتوا
                    </Link>
                </div>
            )}
        </div>
    );
};

export default HistoryPage;